import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal, Stack } from '@mui/material';
import { useEffect } from 'react';
import type { JackpotPool } from '../../../@types';
import { QUERY_KEYS } from '../../../constants';
import { useInvalidateQuery } from '../../../hooks/useInvalidateQuery';
import useLocalization from '../../../hooks/useLocalization';
import JackpotPrizeCard from './JackpotPrizeCard';
import JackpotWinnerArt from './JackpotWinnerArt';

const styles = {
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    px: { xs: 0, md: 3 },
  },
  dialog: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 0,
  },
  panel: {
    position: 'relative',
    display: 'flex',
    width: { xs: '100%', md: 780 },
    maxWidth: '100%',
    maxHeight: '100dvh',
    boxSizing: 'border-box',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
    px: { xs: 0, md: 2 },
    py: { xs: 2, md: 3 },
  },
  closeButton: {
    position: 'absolute',
    top: 'max(16px, env(safe-area-inset-top))',
    right: 'max(16px, env(safe-area-inset-right))',
    width: 44,
    height: 44,
    zIndex: 1,
    color: 'text.secondary',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'text.primary', outlineOffset: 2 },
  },
  closeIcon: {
    fontSize: { xs: 14, md: 18 },
  },
} as const;

type JackpotWinnerAnimationProps = {
  winningJackpotPool: JackpotPool;
  clearJackpot: () => void;
};

const JackpotWinnerAnimation = ({ winningJackpotPool, clearJackpot }: JackpotWinnerAnimationProps) => {
  const { t } = useLocalization();
  const invalidateData = useInvalidateQuery();

  useEffect(() => {
    invalidateData([QUERY_KEYS.jackpots, QUERY_KEYS.balance]);
  }, [invalidateData]);

  return (
    <Modal
      open
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown') {
          clearJackpot();
        }
      }}
      sx={styles.modal}
      data-testid='jackpot-winner-animation'
    >
      <Box role='dialog' aria-modal='true' aria-label={t('winningJackpot', 'Winning Jackpot')} sx={styles.dialog}>
        <IconButton aria-label={t('close')} onClick={clearJackpot} sx={styles.closeButton} disableRipple>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>

        <Stack sx={styles.panel}>
          <JackpotWinnerArt winningJackpotPool={winningJackpotPool} />

          <JackpotPrizeCard winningJackpotPool={winningJackpotPool} />
        </Stack>
      </Box>
    </Modal>
  );
};

export default JackpotWinnerAnimation;
